import { resolvePublicUploadUrl } from "../../../api/apiConfig/apiConfig";

/** Strip competitor / legacy brand names from announcement copy. */
export function toAgcxCopy(text) {
  if (text == null || text === "") return text;
  return String(text)
    .replace(/\bAGCEX\b/g, "AGCX")
    .replace(/\bAGCE\b/g, "AGCX")
    .replace(/\bCoinDCX\b/gi, "AGCX")
    .replace(/\bTACBITS\b/gi, "AGCX")
    .replace(/\bTAXBIYS\b/gi, "AGCX")
    .replace(/\bBybit\b/gi, "AGCX")
    .replace(/\bBinance\b/gi, "AGCX")
    .replace(/\bKuCoin\b/gi, "AGCX")
    .replace(/\bOKX\b/g, "AGCX")
    .replace(/\bWazirX\b/gi, "AGCX")
    .replace(/\bLaunchpool\b/g, "Launchpad");
}

export const FALLBACK_HERO_IMG = "/images/AnnouncementImg/covers/cover_news.svg";
export const FALLBACK_DETAIL_IMG = "/images/AnnouncementImg/covers/cover_news.svg";
export const FALLBACK_EMPTY_IMG = "/images/no_data_vector.svg";

export const CONTENT_COVERS = {
  deposit: "/images/AnnouncementImg/covers/cover_deposit.svg",
  listing: "/images/AnnouncementImg/covers/cover_listing.svg",
  listingPepe: "/images/AnnouncementImg/covers/cover_listing_pepe.svg",
  delist: "/images/AnnouncementImg/covers/cover_delist.svg",
  trading: "/images/AnnouncementImg/covers/cover_trading.svg",
  spot: "/images/AnnouncementImg/covers/cover_spot.svg",
  earn: "/images/AnnouncementImg/covers/cover_earn.svg",
  earnWeek: "/images/AnnouncementImg/covers/cover_earn_week.svg",
  event: "/images/AnnouncementImg/covers/cover_event.svg",
  maintenance: "/images/AnnouncementImg/covers/cover_maintenance.svg",
  vip: "/images/AnnouncementImg/covers/cover_vip.svg",
  web3: "/images/AnnouncementImg/covers/cover_web3.svg",
  news: FALLBACK_DETAIL_IMG,
};

const GENERIC_COVER_HINTS = [
  "announcment_detail_img",
  "hand_speaker_img",
  "topic_items_icon",
  "cover_deposit.png",
  "cover_listing.png",
  "cover_delist.png",
  "cover_trading.png",
  "cover_earn.png",
  "cover_event.png",
  "cover_maintenance.png",
  "cover_vip.png",
  "cover_web3.png",
];

export function coverFromContent(item) {
  const text = `${item?.title || ""} ${item?.description || ""} ${item?.categoryId || item?.category_id || ""} ${item?.categoryTitle || ""}`.toLowerCase();
  const cat = String(item?.categoryId || item?.category_id || "").toLowerCase();

  if (cat.includes("delist") || /market update|listing review/.test(text)) {
    return CONTENT_COVERS.delist;
  }
  if (cat.includes("event") || /carnival|event|reward|campaign|tournament|prize/.test(text)) {
    return CONTENT_COVERS.event;
  }
  if (cat.includes("listing") || /new listed|lists new|listing of|alpha new listed/.test(text)) {
    return CONTENT_COVERS.listing;
  }
  if (/deposit|withdraw|network/.test(text)) {
    return CONTENT_COVERS.deposit;
  }
  if (/tick size|trading pair|spot trading|order book|order experience/.test(text)) {
    return CONTENT_COVERS.trading;
  }
  if (cat.includes("earn") || /earn|apr|apy|staking|yield/.test(text)) {
    return CONTENT_COVERS.earn;
  }
  if (cat.includes("maintenance") || /upgrade|improvement|platform/.test(text)) {
    return CONTENT_COVERS.maintenance;
  }
  if (cat.includes("vip") || cat.includes("institution") || /vip|institution/.test(text)) {
    return CONTENT_COVERS.vip;
  }
  if (cat.includes("web3") || /web3/.test(text)) {
    return CONTENT_COVERS.web3;
  }
  return CONTENT_COVERS.news;
}

function isGenericCover(url) {
  if (!url) return true;
  const s = String(url).toLowerCase();
  return GENERIC_COVER_HINTS.some((hint) => s.includes(hint));
}

const CATEGORY_ICON_FILES = [
  "/images/AnnouncementImg/topic_items_icon1.svg",
  "/images/AnnouncementImg/topic_items_icon2.svg",
  "/images/AnnouncementImg/topic_items_icon3.svg",
  "/images/AnnouncementImg/topic_items_icon4.svg",
  "/images/AnnouncementImg/topic_items_icon5.svg",
];

function firstNonEmpty(...vals) {
  for (const v of vals) {
    if (v == null) continue;
    const s = String(v).trim();
    if (s) return s;
  }
  return "";
}

export function pickAnnouncementMedia(...candidates) {
  for (const c of candidates) {
    const raw = firstNonEmpty(c);
    if (!raw) continue;
    const url = resolvePublicUploadUrl(raw) || raw;
    if (url) return url;
  }
  return "";
}

export function announcementCoverUrl(item, { fallback = true } = {}) {
  const apiUrl = pickAnnouncementMedia(
    item?.image,
    item?.image_path,
    item?.imagePath,
    item?.banner,
    item?.bannerImage,
    item?.banner_image,
    item?.thumbnail,
    item?.cover,
    item?.cover_image,
    item?.photo
  );
  if (apiUrl && !isGenericCover(apiUrl)) return apiUrl;
  const byContent = coverFromContent(item);
  if (byContent) return byContent;
  return fallback ? FALLBACK_DETAIL_IMG : "";
}

export function categoryIconUrl(item, index = 0) {
  return (
    pickAnnouncementMedia(item?.icon, item?.icon_path, item?.logo, item?.logoUrl)
    || CATEGORY_ICON_FILES[index % CATEGORY_ICON_FILES.length]
  );
}

export function rewriteHtmlMedia(html) {
  if (!html) return "";
  return String(html).replace(/<img\b([^>]*?)\bsrc=["']([^"']+)["']/gi, (full, attrs, src) => {
    const abs = resolvePublicUploadUrl(src) || src;
    return `<img${attrs} src="${abs}"`;
  });
}

export function normalizeCategory(item, index = 0) {
  if (!item) return item;
  return {
    ...item,
    _id: item._id || item.id,
    title: toAgcxCopy(item.title || item.name || "Announcements"),
    description: toAgcxCopy(item.description || ""),
    icon: categoryIconUrl(item, index),
  };
}

export function normalizeAnnouncement(item, categoryId) {
  if (!item) return item;
  const created = item.createdAt || item.created_at || item.date || item.publishedAt;
  const ageMs = created ? Date.now() - new Date(created).getTime() : Infinity;
  const isNew = item.isNew ?? item.is_new ?? (Number.isFinite(ageMs) && ageMs >= 0 && ageMs < 3 * 24 * 60 * 60 * 1000);
  const description = toAgcxCopy(item.description || item.content || item.body || "");
  return {
    ...item,
    _id: item._id || item.id,
    title: toAgcxCopy(item.title || ""),
    description: rewriteHtmlMedia(description),
    createdAt: created,
    categoryId: item.categoryId || item.category_id || item.announce_title_id || categoryId,
    image: announcementCoverUrl({
      ...item,
      title: toAgcxCopy(item.title || ""),
      description,
      categoryId: item.categoryId || item.category_id || categoryId,
      categoryTitle: item.categoryTitle,
    }, { fallback: true }),
    isNew: Boolean(isNew),
  };
}

export function extractList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.list)) return payload.list;
  if (Array.isArray(payload?.docs)) return payload.docs;
  if (Array.isArray(payload?.announcements)) return payload.announcements;
  return [];
}

export const STATIC_CATEGORIES = [
  { _id: "cat_latest_announcements", title: "Latest Announcements", icon: "/images/AnnouncementImg/topic_items_icon1.svg" },
  { _id: "cat_latest_events", title: "Latest Events", icon: "/images/AnnouncementImg/topic_items_icon2.svg" },
  { _id: "cat_new_listings", title: "New Listings", icon: "/images/AnnouncementImg/topic_items_icon3.svg" },
  { _id: "cat_product_updates", title: "Product Updates", icon: "/images/AnnouncementImg/topic_items_icon4.svg" },
  { _id: "cat_institutions_vips", title: "Institutions & VIPs", icon: "/images/AnnouncementImg/topic_items_icon5.svg" },
  { _id: "cat_system_maintenance", title: "System Maintenance", icon: "/images/AnnouncementImg/topic_items_icon1.svg" },
  { _id: "cat_delisting", title: "Delisting", icon: "/images/AnnouncementImg/topic_items_icon2.svg" },
  { _id: "cat_web3", title: "Web3", icon: "/images/AnnouncementImg/topic_items_icon3.svg" },
  { _id: "cat_others", title: "Others", icon: "/images/AnnouncementImg/topic_items_icon4.svg" },
  { _id: "cat_history", title: "History", icon: "/images/AnnouncementImg/topic_items_icon5.svg" }
];

export const STATIC_ANNOUNCEMENTS = [
  // Dummy list items are hidden so Latest Announcements, Latest Events,
  // and New Listings show the empty “no announcements” state.
  /*
  {
    _id: "ann_1",
    categoryId: "cat_latest_announcements",
    title: "AGCX Adds New Deposit Networks for BTC and USDT",
    description: "<p>AGCX has expanded deposit support so users can move BTC and USDT onto the platform through additional networks.</p><p>You can continue to deposit, trade, and withdraw as usual. Choose the matching network in Wallet before sending funds, and always use the address shown in your account.</p><p>More network options will be added over time to keep transfers fast and convenient.</p>",
    image: CONTENT_COVERS.deposit,
    createdAt: "2026-08-13T16:02:00.000Z",
    isNew: true
  },
  {
    _id: "ann_2",
    categoryId: "cat_latest_announcements",
    title: "AGCX Alpha New Listed Token — MANLET (MANLET)",
    description: "<p>MANLET (MANLET) is now available on AGCX Alpha.</p><p>Spot trading is open for eligible users. You can view the pair on Markets and start trading from the Alpha board.</p><p>Please complete any required verification before placing orders, and trade only after reviewing the token information on the listing page.</p>",
    image: CONTENT_COVERS.listing,
    createdAt: "2026-08-13T15:36:00.000Z",
    isNew: false
  },
  {
    _id: "ann_3",
    categoryId: "cat_latest_announcements",
    title: "AGCX Rolls Out a Faster Spot Order Experience",
    description: "<p>We have upgraded the Spot trading workspace so orders place and update more quickly across major pairs.</p><p>Open, filled, and cancelled orders now refresh in real time, with clearer status labels in Open Orders and Order History.</p><p>No action is needed on your side. Open Spot trading to use the updated flow.</p>",
    image: CONTENT_COVERS.spot,
    createdAt: "2026-08-13T14:15:00.000Z",
    isNew: false
  },
  {
    _id: "ann_4",
    categoryId: "cat_latest_announcements",
    title: "USDT Earn Pool Is Now Live with Flexible Redemption",
    description: "<p>A new USDT Earn pool is live on AGCX, with flexible redemption so you can subscribe and redeem from Earn at any time, subject to available quota.</p><p>Open Earn, select the USDT pool, and subscribe from your Funding or Spot wallet.</p><p>Displayed APR can change with market demand. Review the pool terms before you subscribe.</p>",
    image: CONTENT_COVERS.earn,
    createdAt: "2026-08-13T14:15:00.000Z",
    isNew: false
  },
  {
    _id: "ann_5",
    categoryId: "cat_latest_announcements",
    title: "AGCX Improves Tick Size on Selected Spot Pairs",
    description: "<p>To improve liquidity and order-book quality, AGCX has refined tick sizes on selected Spot pairs.</p><p>Tighter pricing steps help orders match more smoothly. Existing open orders remain valid and will follow the updated tick size.</p><p>You can review the latest pairs and prices on the Markets page.</p>",
    image: CONTENT_COVERS.trading,
    createdAt: "2026-08-13T13:57:00.000Z",
    isNew: false
  },
  {
    _id: "ann_6",
    categoryId: "cat_latest_announcements",
    title: "Earn Wednesday: Boosted APR on USDT, BTC and TRX",
    description: "<p>This week's Earn Wednesday offers boosted APR on selected USDT, BTC, and TRX products.</p><p>Subscribe from Earn while quota lasts. Rewards accrue according to each product’s terms and can be tracked in your Earn history.</p><p>APR is variable. Read the product details before subscribing.</p>",
    image: CONTENT_COVERS.earnWeek,
    createdAt: "2026-08-12T19:02:00.000Z",
    isNew: false
  },
  {
    _id: "ann_event_1",
    categoryId: "cat_latest_events",
    title: "Join the Summer Trading Carnival",
    description: "<p>The Summer Trading Carnival is live. Trade eligible Spot pairs to unlock prize-pool rewards during the campaign window.</p><p>Volume is counted automatically on qualifying pairs. Progress and rewards appear in the event dashboard after you opt in.</p><p>Terms apply. See the event page for eligible pairs and prize details.</p>",
    image: CONTENT_COVERS.event,
    createdAt: "2026-08-12T10:00:00.000Z",
    isNew: true
  },
  {
    _id: "ann_listing_1",
    categoryId: "cat_new_listings",
    title: "AGCX Lists PEPE (PEPE) for Spot Trading",
    description: "<p>PEPE (PEPE) Spot trading is now live on AGCX.</p><p>Deposits, trading, and withdrawals are available for the listed pair. Open Markets or search PEPE to start trading.</p><p>Always confirm the token contract and pair details on the listing page before you trade.</p>",
    image: CONTENT_COVERS.listingPepe,
    createdAt: "2026-08-11T09:00:00.000Z",
    isNew: false
  }
  */
];
